import { Shield, Swords, Rocket, Crown, AlertTriangle, Check, X, Eye } from "lucide-react";

// Samurai palette — intentionally distinct from the main app
export const S = {
  bg:        "#070709",
  panel:     "#0E0E12",
  panelHi:   "#131319",
  hairline:  "var(--bc-border, rgba(255,255,255,0.07))",
  hairline2: "var(--bc-border, rgba(255,255,255,0.1))",
  ink:       "#F5F5F4",
  muted:     "#6B6B73",
  sub:       "#9A9A9E",

  // Primary neon accents
  blood:     "#FF2E3E",      // more saturated than #DC2626
  bloodDim:  "rgba(255,46,62,0.14)",
  bloodGlow: "rgba(255,46,62,0.55)",
  gold:      "#FFD75A",      // brighter gold
  goldDim:   "rgba(255,215,90,0.12)",
  goldGlow:  "rgba(255,215,90,0.55)",
  steel:     "#9DF6FF",      // cyan neon (was grey)
  steelDim:  "rgba(157,246,255,0.12)",
  steelGlow: "rgba(157,246,255,0.55)",

  // Slot-specific neon
  gundam:    "#B487FF",      // violet-neon for Gundam exact scores
  gundamDim: "rgba(180,135,255,0.12)",
  gundamGlow:"rgba(180,135,255,0.55)",
  shogun:    "#FF6FD8",      // magenta neon (was #8B5CF6)
  shogunDim: "rgba(255,111,216,0.12)",
  shogunGlow:"rgba(255,111,216,0.55)",

  // Ambient neon green (pulsing accents on the background)
  neon:      "#22FFA3",
  neonDim:   "rgba(34,255,163,0.10)",
  neonGlow:  "rgba(34,255,163,0.55)",

  // Outcome badges
  win:       "#22FFA3",      // neon green
  winDim:    "rgba(34,255,163,0.14)",
  loss:      "#FF2E3E",
  lossDim:   "rgba(255,46,62,0.14)",
};

export const SLOT_META = {
  shinobi: {
    label:   "SHINOBI",
    sub:     "il sicuro",
    icon:    Shield,
    color:   S.steel,
    glow:    S.steelGlow,
    bg:      S.steelDim,
  },
  samurai: {
    label:   "SAMURAI",
    sub:     "la combo",
    icon:    Swords,
    color:   S.gold,
    glow:    S.goldGlow,
    bg:      S.goldDim,
  },
  gundam: {
    label:   "GUNDAM",
    sub:     "i risultati esatti",
    icon:    Rocket,
    color:   S.gundam,
    glow:    S.gundamGlow,
    bg:      S.gundamDim,
  },
  shogun: {
    label:   "SHOGUN",
    sub:     "la value bet",
    icon:    Crown,
    color:   S.shogun,
    glow:    S.shogunGlow,
    bg:      S.shogunDim,
  },
  kage: {
    label:   "KAGE",
    sub:     "multigol per squadra",
    icon:    Eye,
    color:   S.neon,
    glow:    S.neonGlow,
    bg:      S.neonDim,
  },
};

export const GRADE_COLOR = {
  S: S.gold, A: "#22FFA3", B: "#9DF6FF", C: "#FF2E3E",
};

export function KatanaDivider({ style = {} }) {
  return (
    <div
      aria-hidden
      style={{
        height: 1,
        background: `linear-gradient(to right, transparent, ${S.blood}80 30%, ${S.blood}80 70%, transparent)`,
        boxShadow: `0 0 12px ${S.bloodGlow}`,
        margin: "0",
        ...style,
      }}
    />
  );
}

/**
 * Black ambient background with pulsing green-neon accents.
 * Rendered once per page, fixed behind content (zIndex: 0).
 */
export function SamuraiBackground() {
  return (
    <>
      <style>{`
        @keyframes samuraiPulse {
          0%, 100% { opacity: 0.35; filter: drop-shadow(0 0 6px ${S.neon}); }
          50%      { opacity: 1;    filter: drop-shadow(0 0 14px ${S.neon}); }
        }
        @keyframes samuraiPulseSoft {
          0%, 100% { opacity: 0.15; }
          50%      { opacity: 0.55; }
        }
        @keyframes samuraiScan {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 0.4; }
          90%  { opacity: 0.4; }
          100% { transform: translateY(100vh); opacity: 0; }
        }
        @keyframes strikeSlashA {
          0%   { transform: translate(-60vw, -60vh) rotate(30deg); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(60vw, 60vh) rotate(30deg);  opacity: 0; }
        }
        @keyframes strikeSlashB {
          0%   { transform: translate(60vw, -60vh) rotate(-30deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translate(-60vw, 60vh) rotate(-30deg); opacity: 0; }
        }
        @keyframes strikeFlash {
          0%   { opacity: 0; }
          35%  { opacity: 0.9; }
          55%  { opacity: 0.45; }
          100% { opacity: 0; }
        }
        @keyframes strikeKanji {
          0%   { opacity: 0; transform: scale(0.4) rotate(-8deg); }
          25%  { opacity: 1; transform: scale(1.25) rotate(0deg); }
          60%  { opacity: 1; transform: scale(1.05) rotate(0deg); }
          100% { opacity: 0; transform: scale(2) rotate(4deg); }
        }
        @keyframes strikeShake {
          0%, 100% { transform: translate(0, 0); }
          20%      { transform: translate(-6px, 2px); }
          40%      { transform: translate(5px, -3px); }
          60%      { transform: translate(-3px, 4px); }
          80%      { transform: translate(4px, -2px); }
        }
        @keyframes bushidoFlashIn {
          0%   { transform: scale(0.6) translateY(-20px); opacity: 0; filter: blur(6px); }
          55%  { transform: scale(1.1) translateY(0); opacity: 1; filter: blur(0); }
          100% { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
        }
        @keyframes bushidoPulse {
          0%, 100% {
            box-shadow: 0 0 18px rgba(34, 255, 163, 0.55),
                        0 0 42px rgba(34, 255, 163, 0.30),
                        inset 0 0 14px rgba(34, 255, 163, 0.25);
          }
          50% {
            box-shadow: 0 0 26px rgba(34, 255, 163, 0.80),
                        0 0 60px rgba(34, 255, 163, 0.45),
                        inset 0 0 20px rgba(34, 255, 163, 0.40);
          }
        }
        @keyframes bushidoShine {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        /* ─── Kurosawa (meditative, slow zen) ─────────────────────────── */
        @keyframes kuroBrush {
          0%   { transform: scaleX(0); opacity: 0; }
          25%  { opacity: 1; }
          100% { transform: scaleX(1); opacity: 1; }
        }
        @keyframes kuroRipple {
          0%   { transform: scale(0.1); opacity: 0.9; }
          100% { transform: scale(4); opacity: 0; }
        }
        @keyframes kuroGlyph {
          0%   { opacity: 0; transform: translateY(18px) scale(0.7); filter: blur(4px); }
          35%  { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          75%  { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          100% { opacity: 0; transform: translateY(-18px) scale(1.08); filter: blur(2px); }
        }
        @keyframes kuroFadeOut {
          0%, 70% { opacity: 1; }
          100%    { opacity: 0; }
        }
        /* ─── Mifune (duel balanced) ──────────────────────────────────── */
        @keyframes mifSlashL {
          0%   { transform: translate(-80vw, -80vh) rotate(40deg); opacity: 0; }
          25%  { opacity: 1; }
          55%  { transform: translate(0, 0) rotate(40deg); opacity: 1; }
          100% { transform: translate(0, 0) rotate(40deg); opacity: 0; }
        }
        @keyframes mifSlashR {
          0%   { transform: translate(80vw, -80vh) rotate(-40deg); opacity: 0; }
          25%  { opacity: 1; }
          55%  { transform: translate(0, 0) rotate(-40deg); opacity: 1; }
          100% { transform: translate(0, 0) rotate(-40deg); opacity: 0; }
        }
        @keyframes mifFlash {
          0%, 50% { opacity: 0; }
          62%     { opacity: 1; }
          100%    { opacity: 0; }
        }
        @keyframes mifSparks {
          0%   { transform: translate(0, 0) scale(0); opacity: 0; }
          55%  { transform: translate(0, 0) scale(0); opacity: 0; }
          70%  { opacity: 1; transform: translate(var(--dx), var(--dy)) scale(1); }
          100% { opacity: 0; transform: translate(calc(var(--dx) * 2), calc(var(--dy) * 2)) scale(0.2); }
        }
        @keyframes mifGlyph {
          0%, 50% { opacity: 0; transform: scale(0.3) rotate(-90deg); }
          65%     { opacity: 1; transform: scale(1.3) rotate(0deg); }
          80%     { transform: scale(1) rotate(0deg); }
          100%    { opacity: 0; transform: scale(1.6) rotate(15deg); }
        }
        /* ─── Naruse (brutal assault) ─────────────────────────────────── */
        @keyframes narStrobe {
          0%, 100% { opacity: 0; }
          20%, 40%, 60% { opacity: 0.7; }
          30%, 50%, 70% { opacity: 0; }
        }
        @keyframes narSlash {
          0%   { transform: translateX(-120%) rotate(var(--rot)); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateX(120%) rotate(var(--rot)); opacity: 0; }
        }
        @keyframes narBloodSplat {
          0%   { transform: translate(0, 0) scale(0); opacity: 0; }
          25%  { opacity: 0; }
          45%  { opacity: 1; transform: translate(var(--dx), var(--dy)) scale(1); }
          100% { opacity: 0; transform: translate(calc(var(--dx) * 1.4), calc(var(--dy) * 1.4)) scale(1.3); }
        }
        @keyframes narGlyph {
          0%, 15% { opacity: 0; transform: scale(0.2); filter: blur(8px); }
          30%     { opacity: 1; transform: scale(1.5); filter: blur(0); }
          55%     { opacity: 1; transform: scale(1.1); filter: blur(0); }
          100%    { opacity: 0; transform: scale(2.2); filter: blur(4px); }
        }
        @keyframes narShake {
          0%, 100% { transform: translate(0, 0); }
          10%  { transform: translate(-8px, 4px); }
          30%  { transform: translate(9px, -5px); }
          50%  { transform: translate(-6px, 6px); }
          70%  { transform: translate(7px, -3px); }
          90%  { transform: translate(-4px, 2px); }
        }
      `}</style>

      {/* solid black background */}
      <div
        aria-hidden
        style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
          backgroundColor: S.bg,
        }}
      />

      {/* faint green aura at bottom-right */}
      <div
        aria-hidden
        style={{
          position: "fixed", right: -220, bottom: -220, width: 640, height: 640,
          background: `radial-gradient(circle, rgba(34,255,163,0.22), transparent 65%)`,
          pointerEvents: "none", zIndex: 0,
          animation: "samuraiPulseSoft 5.5s ease-in-out infinite",
        }}
      />
      {/* faint green aura at top-left */}
      <div
        aria-hidden
        style={{
          position: "fixed", left: -240, top: -200, width: 640, height: 640,
          background: `radial-gradient(circle, rgba(34,255,163,0.18), transparent 65%)`,
          pointerEvents: "none", zIndex: 0,
          animation: "samuraiPulseSoft 6.5s ease-in-out infinite 1.2s",
        }}
      />

      {/* vertical neon pulse on the far right */}
      <div
        aria-hidden
        style={{
          position: "fixed", right: 0, top: 0, bottom: 0, width: 1,
          background: `linear-gradient(to bottom, transparent, ${S.neon} 30%, ${S.neon} 70%, transparent)`,
          boxShadow: `0 0 12px ${S.neonGlow}`,
          pointerEvents: "none", zIndex: 0,
          animation: "samuraiPulse 4.5s ease-in-out infinite",
        }}
      />
      {/* vertical neon pulse on the far left */}
      <div
        aria-hidden
        style={{
          position: "fixed", left: 0, top: 0, bottom: 0, width: 1,
          background: `linear-gradient(to bottom, transparent, ${S.neon} 30%, ${S.neon} 70%, transparent)`,
          boxShadow: `0 0 12px ${S.neonGlow}`,
          pointerEvents: "none", zIndex: 0,
          animation: "samuraiPulse 4.5s ease-in-out infinite 2.2s",
        }}
      />

      {/* 4 neon corner brackets */}
      <CornerBracket position="tl" delay="0s" />
      <CornerBracket position="tr" delay="1.2s" />
      <CornerBracket position="bl" delay="0.6s" />
      <CornerBracket position="br" delay="1.8s" />

      {/* hairline scan-line sweeping slowly */}
      <div
        aria-hidden
        style={{
          position: "fixed", left: 0, right: 0, height: 1,
          background: `linear-gradient(to right, transparent, ${S.neon}, transparent)`,
          boxShadow: `0 0 10px ${S.neonGlow}`,
          pointerEvents: "none", zIndex: 0,
          animation: "samuraiScan 9s linear infinite",
        }}
      />
    </>
  );
}

function CornerBracket({ position, delay }) {
  const OFFSET = 14;
  const LEN = 26;
  const stylesByPos = {
    tl: { top: OFFSET, left: OFFSET, borderTop: `2px solid ${S.neon}`, borderLeft: `2px solid ${S.neon}` },
    tr: { top: OFFSET, right: OFFSET, borderTop: `2px solid ${S.neon}`, borderRight: `2px solid ${S.neon}` },
    bl: { bottom: OFFSET, left: OFFSET, borderBottom: `2px solid ${S.neon}`, borderLeft: `2px solid ${S.neon}` },
    br: { bottom: OFFSET, right: OFFSET, borderBottom: `2px solid ${S.neon}`, borderRight: `2px solid ${S.neon}` },
  };
  return (
    <div
      aria-hidden
      style={{
        position: "fixed", width: LEN, height: LEN,
        pointerEvents: "none", zIndex: 0,
        animation: `samuraiPulse 3.8s ease-in-out infinite ${delay}`,
        ...stylesByPos[position],
      }}
    />
  );
}

export { AlertTriangle, Check, X };
